import * as THREE from "three";
import type { OrbitControls } from "three/addons/controls/OrbitControls.js";
import type { Pano360View } from "./Pano360View.ts";
import { api } from "../api/client.ts";

/** Standing camera height above the floor (m). Default y-offset for raycast-on-mesh placement. */
const FLOOR_OFFSET = 1.55;
const DRAG_THRESHOLD_PX = 4;

/**
 * Phase D — manual alignment for 360° panoramas.
 *
 * Two interactions:
 * 1. **Drag a marker in 3D** (dollhouse view): raycasts on mesh under the
 *    pointer, snaps the marker to that XZ + `floorY + FLOOR_OFFSET`.
 *    In-memory only; persists on Save.
 * 2. **Click a marker** (no drag): enters pano session + opens the yaw editor.
 *    Slider rotates the sphere live; Save → PATCH; Cancel → revert.
 *
 * Reverts use a per-node original snapshot captured on first edit.
 */
export class Pano360Align {
  private active_ = false;
  private projectId = "";

  // Drag state
  private dragNodeId = -1;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragMoved = false;

  // Edit state (when pano is open)
  private editingNodeId = -1;

  // Per-node snapshots for Cancel.
  private originals = new Map<number, { position: THREE.Vector3; yaw: number; aligned: boolean }>();

  // DOM
  private editor: HTMLElement;
  private yawSlider: HTMLInputElement;
  private yawValue: HTMLElement;
  private posLabel: HTMLElement;
  private idLabel: HTMLElement;
  private saveBtn: HTMLButtonElement;
  private cancelBtn: HTMLButtonElement;

  private raycaster = new THREE.Raycaster();
  private wasControlsEnabled = true;

  /** Fires when the operator hits Save successfully. */
  onSaved: ((nodeId: number) => void) | null = null;

  constructor(
    private camera: THREE.PerspectiveCamera,
    private renderer: THREE.WebGLRenderer,
    private controls: OrbitControls,
    private panoView: Pano360View,
    private meshes: THREE.Mesh[],
  ) {
    this.editor = document.getElementById("pano-align-editor")!;
    this.yawSlider = this.editor.querySelector(".pa-yaw-slider") as HTMLInputElement;
    this.yawValue = this.editor.querySelector(".pa-yaw-value") as HTMLElement;
    this.posLabel = this.editor.querySelector(".pa-pos-label") as HTMLElement;
    this.idLabel = this.editor.querySelector(".pa-id-label") as HTMLElement;
    this.saveBtn = this.editor.querySelector(".pa-save") as HTMLButtonElement;
    this.cancelBtn = this.editor.querySelector(".pa-cancel") as HTMLButtonElement;

    this.yawSlider.addEventListener("input", () => this._onYawSliderChange());
    this.saveBtn.addEventListener("click", () => { void this._save(); });
    this.cancelBtn.addEventListener("click", () => this._cancel());

    const c = this.renderer.domElement;
    // Capture phase so we can disable orbit controls on a marker hit before they react.
    c.addEventListener("pointerdown", this._onPointerDown, true);
    c.addEventListener("pointermove", this._onPointerMove, true);
    c.addEventListener("pointerup", this._onPointerUp, true);
  }

  setProjectId(id: string): void { this.projectId = id; }

  setMeshes(meshes: THREE.Mesh[]): void {
    this.meshes = meshes;
  }

  get isActive(): boolean { return this.active_; }

  setActive(on: boolean): void {
    if (this.active_ === on) return;
    this.active_ = on;
    if (on) {
      this.panoView.setMarkersVisible(true);
    } else {
      // Discard any in-flight edit
      if (this.editingNodeId >= 0) this._cancel();
      this.editor.classList.remove("open");
    }
  }

  /** Fired by Viewer3D when a panorama session opens (any node). */
  notifyPanoEntered(nodeId: number): void {
    if (!this.active_) return;
    this._beginEdit(nodeId);
  }

  /** Fired by Viewer3D when a panorama session exits. */
  notifyPanoExited(): void {
    if (this.editingNodeId >= 0) {
      // Treat exit-without-save as cancel to keep state consistent.
      this._cancel();
    }
  }

  dispose(): void {
    const c = this.renderer.domElement;
    c.removeEventListener("pointerdown", this._onPointerDown, true);
    c.removeEventListener("pointermove", this._onPointerMove, true);
    c.removeEventListener("pointerup", this._onPointerUp, true);
  }

  // ── Pointer handling: drag-vs-click on markers ───────────────────────────

  private _ndcFromEvent(e: PointerEvent): THREE.Vector2 {
    const r = this.renderer.domElement.getBoundingClientRect();
    return new THREE.Vector2(
      ((e.clientX - r.left) / r.width) * 2 - 1,
      -((e.clientY - r.top) / r.height) * 2 + 1,
    );
  }

  private _onPointerDown = (e: PointerEvent): void => {
    if (!this.active_ || e.button !== 0) return;
    if (this.panoView.isPanoActive) return; // pano session owns input
    const id = this.panoView.pickMarker(this._ndcFromEvent(e), this.raycaster);
    if (id < 0) return;

    e.stopImmediatePropagation();
    this.dragNodeId = id;
    this.dragStartX = e.clientX;
    this.dragStartY = e.clientY;
    this.dragMoved = false;
    this.wasControlsEnabled = this.controls.enabled;
    this.controls.enabled = false;
    this.renderer.domElement.setPointerCapture(e.pointerId);
    this.renderer.domElement.style.cursor = "grabbing";
  };

  private _onPointerMove = (e: PointerEvent): void => {
    if (this.dragNodeId < 0) return;
    e.stopImmediatePropagation();
    const dx = e.clientX - this.dragStartX;
    const dy = e.clientY - this.dragStartY;
    if (!this.dragMoved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
    this.dragMoved = true;

    // Raycast mesh under the pointer; place marker at hit + standing offset.
    const ndc = this._ndcFromEvent(e);
    this.raycaster.setFromCamera(ndc, this.camera);
    const hits = this.raycaster.intersectObjects(this.meshes, true);
    if (!hits.length) return;

    const hit = hits[0].point;
    this._captureOriginal(this.dragNodeId);
    const newPos = new THREE.Vector3(hit.x, hit.y + FLOOR_OFFSET, hit.z);
    this.panoView.applyNodeUpdate(this.dragNodeId, { position: newPos });
  };

  private _onPointerUp = (e: PointerEvent): void => {
    if (this.dragNodeId < 0) return;
    e.stopImmediatePropagation();
    const id = this.dragNodeId;
    const wasMoved = this.dragMoved;

    this.renderer.domElement.releasePointerCapture(e.pointerId);
    this.renderer.domElement.style.cursor = "";
    this.controls.enabled = this.wasControlsEnabled;
    this.dragNodeId = -1;
    this.dragMoved = false;

    if (!wasMoved) {
      // Click → open pano session + editor (notifyPanoEntered will run _beginEdit).
      void this.panoView.enterPanoramaAtNode(id);
    }
    // If dragged: position update already applied; operator must click marker
    // again to open the yaw editor & Save.
  };

  // ── Edit lifecycle ───────────────────────────────────────────────────────

  private _captureOriginal(id: number): void {
    if (this.originals.has(id)) return;
    const data = this.panoView.getNodeData(id);
    if (!data) return;
    this.originals.set(id, {
      position: new THREE.Vector3(data.position.x, data.position.y, data.position.z),
      yaw: data.yaw ?? 0,
      aligned: data.aligned !== false,
    });
  }

  private _beginEdit(nodeId: number): void {
    this.editingNodeId = nodeId;
    this._captureOriginal(nodeId);
    const data = this.panoView.getNodeData(nodeId);
    if (!data) return;
    this.idLabel.textContent = `Pano #${nodeId}`;
    this._renderPosLabel(data.position);
    const yawDeg = Math.round(THREE.MathUtils.radToDeg(data.yaw ?? 0));
    this.yawSlider.value = String(yawDeg);
    this.yawValue.textContent = `${yawDeg}°`;
    this.editor.classList.add("open");
  }

  private _renderPosLabel(p: { x: number; y: number; z: number }): void {
    this.posLabel.textContent = `x=${p.x.toFixed(2)}  y=${p.y.toFixed(2)}  z=${p.z.toFixed(2)}`;
  }

  private _onYawSliderChange(): void {
    if (this.editingNodeId < 0) return;
    const yawDeg = parseFloat(this.yawSlider.value);
    const yawRad = THREE.MathUtils.degToRad(yawDeg);
    this.yawValue.textContent = `${Math.round(yawDeg)}°`;
    // Don't write to manifest yet — only update the live sphere; manifest is
    // updated on Save (so Cancel can drop the slider value cleanly).
    this.panoView.setLiveSphereYaw(yawRad);
  }

  private async _save(): Promise<void> {
    if (this.editingNodeId < 0 || !this.projectId) return;
    const id = this.editingNodeId;
    const data = this.panoView.getNodeData(id);
    if (!data) return;
    const yawDeg = parseFloat(this.yawSlider.value);
    const yawRad = THREE.MathUtils.degToRad(yawDeg);

    this.saveBtn.disabled = true;
    this.cancelBtn.disabled = true;
    try {
      await api.pano360.align(this.projectId, id, {
        position: data.position,
        yaw: yawRad,
        aligned: true,
      });
      // Apply locally too (in-memory manifest + marker colour + source).
      this.panoView.applyNodeUpdate(id, {
        position: new THREE.Vector3(data.position.x, data.position.y, data.position.z),
        yaw: yawRad,
        aligned: true,
      });
      this.originals.delete(id); // Cancel after Save would be a no-op.
      this.onSaved?.(id);
    } catch (err) {
      console.error("[Pano360Align] save failed:", err);
    } finally {
      this.saveBtn.disabled = false;
      this.cancelBtn.disabled = false;
    }

    this._closeEditor();
    this.panoView.exitPanorama();
  }

  private _cancel(): void {
    if (this.editingNodeId < 0) return;
    const id = this.editingNodeId;
    const orig = this.originals.get(id);
    if (orig) {
      this.panoView.applyNodeUpdate(id, {
        position: orig.position,
        yaw: orig.yaw,
        aligned: orig.aligned,
      });
      this.originals.delete(id);
    }
    this._closeEditor();
    if (this.panoView.isPanoActive) this.panoView.exitPanorama();
  }

  private _closeEditor(): void {
    this.editor.classList.remove("open");
    this.editingNodeId = -1;
  }
}
